import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Youtube, Instagram, Facebook, BadgeCheck } from "lucide-react";
import { SITE_CONFIG, SOCIAL_LINKS } from "@/data/siteData";
import { AnimateOnScroll, SectionLabel } from "@/components/shared";
import { AnimatedCounter } from "@/components/AnimatedCounter";
import FeaturedContent from "@/components/FeaturedContent";
import Marquee from "react-fast-marquee";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TikTokIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 0 0-.79-.05A6.34 6.34 0 0 0 3.15 15a6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.34-6.34V8.87a8.16 8.16 0 0 0 4.77 1.52V6.96a4.84 4.84 0 0 1-1.01-.27z"/>
  </svg>
);

const SocialIcon = ({ platform, size = 20 }) => {
  switch (platform) {
    case "youtube":
    case "youtubeShorts":
      return <Youtube size={size} />;
    case "tiktok":
      return <TikTokIcon size={size} />;
    case "instagram":
      return <Instagram size={size} />;
    case "facebook":
      return <Facebook size={size} />;
    default:
      return null;
  }
};

const PLATFORM_CONFIG = [
  { key: "youtubeMain", label: "YouTube", url: SOCIAL_LINKS.youtube.url, icon: Youtube, color: "#FF0000" },
  { key: "youtubeShorts", label: "YT Shorts", url: SOCIAL_LINKS.youtubeShorts.url, icon: Youtube, color: "#FF4444" },
  { key: "tiktok", label: "TikTok", url: SOCIAL_LINKS.tiktok.url, icon: TikTokIcon, color: "#ffffff" },
  { key: "instagram", label: "Instagram", url: SOCIAL_LINKS.instagram.url, icon: Instagram, color: "#E1306C" },
  { key: "facebook", label: "Facebook", url: SOCIAL_LINKS.facebook.url, icon: Facebook, color: "#1877F2" },
];

function HeroSection({ followers, heroImage, heroMobileFocus, heroDesktopFocus, tagline, heroDescription }) {
  const [imgLoaded, setImgLoaded] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Track viewport size to pick the right focal point
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener?.("change", update);
    return () => mq.removeEventListener?.("change", update);
  }, []);

  // Reset loaded state when image URL changes
  useEffect(() => {
    setImgLoaded(false);
    if (!heroImage) return;
    const img = new Image();
    img.src = heroImage;
    img.onload = () => setImgLoaded(true);
    img.onerror = () => setImgLoaded(false);
  }, [heroImage]);

  const objectPosition = isMobile
    ? (heroMobileFocus || "50% 50%")
    : (heroDesktopFocus || "center 30%");

  return (
    <section data-testid="hero-section" className="relative min-h-screen flex items-end overflow-hidden bg-[#050505]">
      {/* Background Image — fades in only after the real hero image fully loads, preventing flash of default */}
      <div className="absolute inset-0">
        {heroImage && (
          <img
            src={heroImage}
            alt="Tech Studio"
            className={`w-full h-full object-cover transition-opacity duration-700 ${imgLoaded ? "opacity-100" : "opacity-0"}`}
            style={{ objectPosition }}
            onLoad={() => setImgLoaded(true)}
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/60 to-[#050505]" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/20 to-transparent md:from-black/60 md:via-transparent md:to-transparent" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 pb-12 md:pb-16 w-full">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-2xl"
        >
          <motion.span
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="font-mono text-xs uppercase tracking-[0.25em] text-[#E63946] mb-4 block"
          >
            {tagline}
          </motion.span>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.7 }}
            className="font-heading text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter text-white leading-[0.95]"
          >
            {SITE_CONFIG.name}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="mt-6 text-base md:text-lg text-zinc-400 leading-relaxed max-w-lg"
          >
            {heroDescription}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
            className="mt-8 flex flex-wrap gap-4"
          >
            <Link
              to="/portfolio"
              data-testid="hero-view-work-btn"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#E63946] text-white font-semibold tracking-wide hover:bg-[#F25C69] transition-colors duration-200"
            >
              View Work
              <ArrowRight size={18} />
            </Link>
            <Link
              to="/contact"
              data-testid="hero-contact-btn"
              className="inline-flex items-center gap-2 px-8 py-4 border border-white/20 text-white font-semibold tracking-wide hover:border-white/50 hover:bg-white/5 transition-all duration-200"
            >
              Contact Me
            </Link>
          </motion.div>
        </motion.div>

        {/* ── Follower Stats: below CTA, part of hero ── */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.0, duration: 0.7 }}
          data-testid="platform-stats"
          className="mt-12 md:mt-14 flex flex-col items-center text-center"
        >
          {/* Total Audience */}
          <div className="flex flex-col items-center mb-5">
            <span className="font-mono text-[10px] md:text-xs uppercase tracking-[0.25em] text-zinc-500 mb-2">
              Total Audience
            </span>
            <h2
              className="font-heading text-5xl sm:text-6xl md:text-7xl font-black tracking-tighter text-white"
              data-testid="total-audience-count"
              style={{ textShadow: "0 0 60px rgba(230, 57, 70, 0.25), 0 0 120px rgba(230, 57, 70, 0.1)" }}
            >
              {followers ? (
                <AnimatedCounter target={followers.total} duration={2500} suffix="+" />
              ) : (
                "400K+"
              )}
            </h2>
          </div>

          {/* Platform Breakdown — responsive: grid on mobile, row on desktop */}
          <div className="grid grid-cols-3 gap-2 sm:flex sm:flex-wrap sm:items-center sm:justify-center sm:gap-x-6 sm:gap-y-3 md:gap-x-8">
            {PLATFORM_CONFIG.map(({ key, label, url, icon: Icon, color }) => (
              <a
                key={key}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`platform-stat-${key}`}
                className="group flex items-center gap-1.5 sm:gap-2 transition-opacity hover:opacity-100 opacity-70"
              >
                <span style={{ color }} className="shrink-0">
                  <Icon size={12} />
                </span>
                <span className="text-xs sm:text-sm text-zinc-300 font-medium" data-testid={`follower-count-${key}`}>
                  {followers ? (
                    <AnimatedCounter target={followers[key]} duration={2000} />
                  ) : (
                    "—"
                  )}
                </span>
                <span className="text-[10px] sm:text-xs text-zinc-500 hidden sm:inline">{label}</span>
                <BadgeCheck size={11} className="text-[#1D9BF0] shrink-0 hidden sm:block" />
              </a>
            ))}
          </div>
        </motion.div>

        {/* Social links floating */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2 }}
          className="absolute right-6 lg:right-8 bottom-12 md:bottom-16 hidden md:flex flex-col gap-4"
        >
          {Object.entries(SOCIAL_LINKS).map(([key, social]) => (
            <a
              key={key}
              href={social.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`hero-social-${key}`}
              className="p-2 text-zinc-500 hover:text-[#E63946] transition-colors duration-200"
            >
              <SocialIcon platform={key} />
            </a>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

function BrandPartnerships() {
  const [brands, setBrands] = useState([]);

  useEffect(() => {
    axios.get(`${API}/content/brands`).then(r => setBrands(r.data.items || [])).catch(() => {});
  }, []);

  if (brands.length === 0) return null;

  // Split brands into 3 rows with organic distribution
  const row1 = [], row2 = [], row3 = [];
  brands.forEach((b, i) => {
    if (i % 3 === 0) row1.push(b);
    else if (i % 3 === 1) row2.push(b);
    else row3.push(b);
  });
  // Duplicate for seamless scroll if needed
  const padRow = (r) => r.length < 4 ? [...r, ...r, ...r] : [...r, ...r];

  const BrandLogo = ({ brand }) => {
    const inner = (
      <div className="group relative flex items-center justify-center w-[110px] sm:w-[150px] md:w-[180px] h-10 sm:h-14 md:h-16 mx-2 sm:mx-3 md:mx-4 shrink-0">
        <div className="absolute inset-0 rounded-xl bg-white/0 group-hover:bg-white/[0.06] transition-all duration-500" />
        {brand.logo ? (
          <img
            src={brand.logo}
            alt={brand.name}
            className="max-h-full max-w-full object-contain relative z-10 opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500"
            loading="lazy"
          />
        ) : (
          <span className="font-heading text-xs sm:text-base md:text-lg font-bold text-zinc-400 group-hover:text-white whitespace-nowrap relative z-10 transition-all duration-500">
            {brand.name}
          </span>
        )}
      </div>
    );

    if (brand.link) {
      return (
        <a href={brand.link} target="_blank" rel="noopener noreferrer" data-testid={`brand-link-${brand.id}`}>
          {inner}
        </a>
      );
    }
    return inner;
  };

  return (
    <section className="py-10 md:py-20 border-y border-white/[0.03] bg-[#070708] overflow-hidden" data-testid="brand-partnerships">
      {/* Title */}
      <div className="text-center mb-8 md:mb-14">
        <h3 className="font-heading text-xs sm:text-sm md:text-base font-semibold uppercase tracking-[0.35em] text-zinc-500">
          Brand Partnerships
        </h3>
        <div className="mt-3 mx-auto w-12 h-[2px] bg-[#E63946]/40" />
      </div>

      {/* 3 Rows — unified speed, alternating direction, fixed-width slots prevent overlap */}
      <div className="space-y-3 sm:space-y-5 md:space-y-6">
        <Marquee speed={30} gradient={false} pauseOnHover direction="left">
          {padRow(row1).map((b, i) => (<BrandLogo key={`r1-${b.id}-${i}`} brand={b} />))}
        </Marquee>

        {row2.length > 0 && (
          <Marquee speed={30} gradient={false} pauseOnHover direction="right">
            {padRow(row2).map((b, i) => (<BrandLogo key={`r2-${b.id}-${i}`} brand={b} />))}
          </Marquee>
        )}

        {row3.length > 0 && (
          <Marquee speed={30} gradient={false} pauseOnHover direction="left">
            {padRow(row3).map((b, i) => (<BrandLogo key={`r3-${b.id}-${i}`} brand={b} />))}
          </Marquee>
        )}
      </div>

      {/* See All button */}
      <div className="mt-8 md:mt-12 flex justify-center">
        <Link
          to="/brands"
          data-testid="brands-see-all-home"
          className="group inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-white/15 text-xs sm:text-sm font-mono uppercase tracking-[0.2em] text-zinc-400 hover:text-white hover:border-[#E63946] hover:bg-[#E63946]/5 transition-all duration-300"
        >
          See All Brands
          <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform duration-300" />
        </Link>
      </div>
    </section>
  );
}

function CTASection() {
  return (
    <section className="py-24 md:py-32 bg-[#050505]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
        <AnimateOnScroll>
          <SectionLabel text="Collaborate" />
          <h2 className="mt-4 font-heading text-3xl md:text-5xl font-black tracking-tighter text-white">
            Let's Create Something<br />
            <span className="text-[#E63946]">Extraordinary</span>
          </h2>
          <p className="mt-6 text-zinc-400 max-w-md mx-auto">
            Looking for a creative partner for your next tech campaign? Let's talk.
          </p>
          <Link
            to="/contact"
            data-testid="cta-contact-btn"
            className="mt-8 inline-flex items-center gap-2 px-10 py-4 bg-[#E63946] text-white font-semibold tracking-wide hover:bg-[#F25C69] transition-colors duration-200"
          >
            Get In Touch
            <ArrowRight size={18} />
          </Link>
        </AnimateOnScroll>
      </div>
    </section>
  );
}

export default function Home() {
  const [followers, setFollowers] = useState(null);
  const [heroImage, setHeroImage] = useState("");
  const [heroMobileFocus, setHeroMobileFocus] = useState("50% 50%");
  const [heroDesktopFocus, setHeroDesktopFocus] = useState("center 30%");
  const [tagline, setTagline] = useState(SITE_CONFIG.tagline);
  const [heroDescription, setHeroDescription] = useState(SITE_CONFIG.heroDescription);

  useEffect(() => {
    axios.get(`${API}/followers`).then((res) => setFollowers(res.data)).catch(() => {});
    axios.get(`${API}/content/about`).then((res) => {
      setHeroImage(res.data.heroImage || SITE_CONFIG.heroImage);
      if (res.data.heroMobileFocus) setHeroMobileFocus(res.data.heroMobileFocus);
      if (res.data.heroDesktopFocus) setHeroDesktopFocus(res.data.heroDesktopFocus);
      if (res.data.tagline) setTagline(res.data.tagline);
      if (res.data.heroDescription) setHeroDescription(res.data.heroDescription);
    }).catch(() => setHeroImage(SITE_CONFIG.heroImage));
  }, []);

  return (
    <main data-testid="home-page">
      <HeroSection
        followers={followers}
        heroImage={heroImage}
        heroMobileFocus={heroMobileFocus}
        heroDesktopFocus={heroDesktopFocus}
        tagline={tagline}
        heroDescription={heroDescription}
      />
      <BrandPartnerships />
      <FeaturedContent />
      <CTASection />
    </main>
  );
}
